// src/pages/AffiliateCompanyDetailPage.jsx
// ORBIS PMO — C2.5.2 Ficha 360 Cámara / Organismo
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { hasPermission } from "../utils/permissions";
import { API_BASE } from "../config/orbisConfig";



const STATUS_OPTIONS = [
    "Prospecto",
    "Contactado por email",
    "Contactado por teléfono",
    "Contactado por videollamada",
    "Invitación a reunión",
    "Interés institucional",
    "Aceptó afiliarse",
    "Documentación solicitada",
    "Registro iniciado",
    "Afiliado (sin pago)",
    "Afiliado (pago pendiente)",
    "Afiliado activo (con estrategia)",
    "Afiliado activo (sin estrategia)",
    "Baja",
];

function authHeaders(extra = {}) {
    const token = localStorage.getItem("cani_auth") || "";
    return {
        ...extra,
        Authorization: `Bearer ${token}`,
        "X-CANI-AUTH": token,
    };
}

async function safeJson(url, options = {}) {
    const res = await fetch(url, options);
    const text = await res.text();

    if (!text) throw new Error(`Respuesta vacía HTTP ${res.status}`);

    let data;
    try {
        data = JSON.parse(text);
    } catch {
        throw new Error(`Respuesta no JSON HTTP ${res.status}: ${text.slice(0, 180)}`);
    }

    if (!res.ok || data?.ok === false || data?.success === false) {
        throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
    }

    return data;
}

function fullName(c) {
    return c?.full_name || [c?.first_name, c?.last_name].filter(Boolean).join(" ").trim() || "Sin nombre";
}

function formatHumanDate(value, fallback = "—") {
    if (!value) return fallback;

    const normalized = String(value).replace(" ", "T");
    const d = new Date(normalized);

    if (Number.isNaN(d.getTime())) return String(value);

    const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const yyyy = d.getFullYear();
    const mmm = months[d.getMonth()];
    const dd = String(d.getDate()).padStart(2, "0");

    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const hasTime = !/00:00:00$/.test(String(value)) && !/T00:00$/.test(normalized);

    return hasTime ? `${dd}-${mmm}-${yyyy} ${hh}:${mm}` : `${dd}-${mmm}-${yyyy}`;
}

function isAgendaClosed(item) {
    return ["realizada", "cancelada", "cerrada"].includes(String(item?.status || "").toLowerCase());
}

function agendaDateValue(item) {
    return item?.next_action_date || item?.scheduled_at || item?.created_at || "";
}

function displayIndustry(company) {
    const code = company?.industry_code || "";
    const name = company?.industry_name || "";
    if (code && name) return `${code}: ${name}`;
    return code || name || "Sin eje";
}

function scoreTone(level) {
    if (level === "alta") return "green";
    if (level === "media") return "amber";
    return "red";
}

function badgeToneClass(tone) {
    const map = {
        green: "bg-[#0b3a36] text-[#7cebd0] border-[#10b981]/40",
        amber: "bg-[#3a3110] text-[#f6d76b] border-[#d4af37]/45",
        red: "bg-[#38161c] text-[#fca5a5] border-[#ef4444]/40",
        blue: "bg-[#0b3552] text-[#7dd3fc] border-[#0ea5e9]/50",
        gray: "bg-[#0f2d48] text-[#d9e8f5] border-[#24537a]",
        purple: "bg-[#24163d] text-[#c4b5fd] border-[#8b5cf6]/40",
    };
    return map[tone] || map.gray;
}

function Badge({ children, tone = "gray" }) {
    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${badgeToneClass(tone)}`}>
            {children}
        </span>
    );
}

function Kpi({ title, value, subtitle, tone = "blue" }) {
    const toneClass = badgeToneClass(tone);
    return (
        <div className={`rounded-[22px] border p-4 shadow-[0_12px_30px_rgba(0,0,0,.18)] ${toneClass}`}>
            <div className="text-[11px] opacity-90">{title}</div>
            <div className="text-2xl font-black mt-1">{value}</div>
            {subtitle ? <div className="text-[10px] opacity-80 mt-1">{subtitle}</div> : null}
        </div>
    );
}

function Section({ title, subtitle, right, children }) {
    return (
        <div className="bg-[#04192b] border border-[#0b4d78] rounded-[24px] shadow-[0_12px_30px_rgba(0,0,0,.24)] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#0b4d78] flex items-start justify-between gap-3 bg-[#031425]">
                <div>
                    <h3 className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#d4af37]">{title}</h3>
                    {subtitle ? <p className="text-[11px] text-[#9fc4df] mt-1">{subtitle}</p> : null}
                </div>
                {right ? <div>{right}</div> : null}
            </div>
            {children}
        </div>
    );
}

function Style() {
    return (
        <style>{`
            .affiliate-360 {
                background:#041426 !important;
                color:#f5fbff !important;
            }

            .affiliate-360 table {
                border-collapse: separate !important;
                border-spacing: 0 !important;
                background:#061a2f !important;
            }

            .affiliate-360 table thead th {
                position: sticky;
                top: 0;
                z-index: 10;
                background:#061a2f !important;
                color:#8fb9da !important;
                border-bottom:1px solid #24587c !important;
                text-transform:uppercase;
                letter-spacing:.13em;
            }

            .affiliate-360 table tbody tr {
                transition: background .18s ease, box-shadow .18s ease;
                background:#08233d !important;
            }

            .affiliate-360 table tbody tr:nth-child(even) {
                background:#092a49 !important;
            }

            .affiliate-360 table tbody tr:hover {
                background:rgba(56,189,248,.075) !important;
                box-shadow:inset 3px 0 0 #d4af37 !important;
            }

            .affiliate-360 table tbody tr:hover td,
            .affiliate-360 table tbody td {
                background:transparent !important;
                color:#f5fbff !important;
                border-top:1px solid #24587c !important;
            }

            .affiliate-360 .field {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .affiliate-360 .field label,
            .affiliate-360 label {
                font-size: 11px !important;
                color: #8fb9da !important;
                font-weight:700 !important;
            }

            .affiliate-360 input,
            .affiliate-360 select,
            .affiliate-360 textarea {
                border: 1px solid #0b5f91 !important;
                border-radius: 12px !important;
                padding: 9px 11px !important;
                font-size: 12px !important;
                outline: none !important;
                background: #0b2740 !important;
                color:#ffffff !important;
            }

            .affiliate-360 input::placeholder,
            .affiliate-360 textarea::placeholder {
                color:#7ea8c7 !important;
            }

            .affiliate-360 input:focus,
            .affiliate-360 select:focus,
            .affiliate-360 textarea:focus {
                border-color: #d4af37 !important;
                box-shadow: 0 0 0 2px rgba(212,175,55,.18) !important;
            }

            .affiliate-360 select option {
                background:#061a2f !important;
                color:#fff !important;
            }

            .affiliate-360 input[type="file"] {
                background:#0b2740 !important;
                color:#d7e8f6 !important;
            }

            .affiliate-360 .bg-[#04192b],
            .affiliate-360 .bg-gray-50,
            .affiliate-360 .bg-slate-50 {
                background:#04192b !important;
                color:#f5fbff !important;
            }

            .affiliate-360 .border-[#0b4d78],
            .affiliate-360 .border-gray-200,
            .affiliate-360 .border-slate-200 {
                border-color:#0b4d78 !important;
            }

            .affiliate-360 .text-white,
            .affiliate-360 .text-gray-900,
            .affiliate-360 .text-slate-900 {
                color:#fff !important;
            }

            .affiliate-360 .text-[#9fc4df],
            .affiliate-360 .text-[#d7e8f6],
            .affiliate-360 .text-[#d9e8f5],
            .affiliate-360 .text-slate-500,
            .affiliate-360 .text-slate-600 {
                color:#9fc4df !important;
            }

            .affiliate-360 .hover\:bg-cani-bg:hover,
            .affiliate-360 .hover\:bg-[#04192b]:hover,
            .affiliate-360 .hover\:bg-gray-50:hover {
                background:#10324f !important;
            }

            .affiliate-360 button {
                border-radius:999px !important;
                font-weight:700 !important;
            }

            .affiliate-360 .bg-[#d4af37],
            .affiliate-360 .btn-primary {
                background:#d4af37 !important;
                color:#081a2b !important;
                border-color:#d4af37 !important;
            }

            .affiliate-360 .btn-secondary,
            .affiliate-360 button:not(.btn-primary):not(.unstyled) {
                background:#0b2740 !important;
                color:#f5fbff !important;
                border:1px solid #24537a !important;
            }

            .affiliate-360 .btn-secondary:hover,
            .affiliate-360 button:not(.btn-primary):not(.unstyled):hover {
                background:#10324f !important;
                border-color:#d4af37 !important;
            }

            .affiliate-360 [style*="background: #fff"],
            .affiliate-360 [style*="background:#fff"],
            .affiliate-360 [style*="background: #ffffff"],
            .affiliate-360 [style*="background:#ffffff"],
            .affiliate-360 [style*="background: white"],
            .affiliate-360 [style*="background:white"] {
                background:#08233d !important;
                color:#f5fbff !important;
            }

            .affiliate-360 [style*="color: #111827"],
            .affiliate-360 [style*="color:#111827"],
            .affiliate-360 [style*="color: rgb(17, 24, 39)"] {
                color:#f5fbff !important;
            }

            .affiliate-360 [style*="color: #666"],
            .affiliate-360 [style*="color:#666"],
            .affiliate-360 [style*="color: #6b7280"],
            .affiliate-360 [style*="color:#6b7280"] {
                color:#9fc4df !important;
            }
        `}</style>
    );
}

export default function AffiliateCompanyDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const canEdit = hasPermission("affiliate_companies.edit");
    const role = String(localStorage.getItem("role") || "pm").toLowerCase();
    const canAdmin = role === "admin" || role === "administrador";
    const initialTab = searchParams.get("tab") || "resumen";

    const [tab, setTab] = useState(initialTab);
    const [company, setCompany] = useState(null);
    const [summary, setSummary] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [activities, setActivities] = useState([]);
    const [structure, setStructure] = useState([]);
    const [legacyStructure, setLegacyStructure] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [documentForm, setDocumentForm] = useState({
        document_category: "Expediente institucional",
        title: "",
        description: "",
        doc_type: "file",
        url: "",
        file: null,
    });
    const [editOpen, setEditOpen] = useState(false);
    const [savingCompany, setSavingCompany] = useState(false);
    const [companyDraft, setCompanyDraft] = useState(null);
    const [industries, setIndustries] = useState([]);
    const [sections, setSections] = useState([]);
    const [pmUsers, setPmUsers] = useState([]);

    const [loading, setLoading] = useState(true);
    const [savingActivity, setSavingActivity] = useState(false);
    const [masterContacts, setMasterContacts] = useState([]);
    const [masterContactsLoading, setMasterContactsLoading] = useState(false);
    const [linkingContact, setLinkingContact] = useState(false);
    const [contactForm, setContactForm] = useState({
        contact_id: "",
        is_primary: false,
        role_type: "contacto_clave",
        decision_level: "influenciador",
        position_title: "",
        area: "",
        notes: "",
    });
    const [editingLinkId, setEditingLinkId] = useState(null);
    const [contactEditorOpen, setContactEditorOpen] = useState(false);
    const [contactEditorMode, setContactEditorMode] = useState("new");
    const [savingContactMaster, setSavingContactMaster] = useState(false);
    const [contactDraft, setContactDraft] = useState({
        id: "",
        link_id: "",
        first_name: "",
        last_name: "",
        full_name: "",
        email: "",
        phone: "",
        mobile: "",
        position: "",
        area: "",
        linkedin: "",
        relationship_status: "frio",
        status: "activo",
        notes: "",
        role_type: "contacto_clave",
        decision_level: "influenciador",
        position_title: "",
        link_area: "",
        link_notes: "",
        is_primary: false,
    });
    const [error, setError] = useState("");
    const [toast, setToast] = useState("");

    const [activityForm, setActivityForm] = useState({
        type: "seguimiento",
        channel: "whatsapp",
        subject: "",
        description: "",
        scheduled_at: "",
        next_action: "",
        next_action_date: "",
        status: "programada",
        priority: "media",
        result: "",
    });

    const [agendaModalOpen, setAgendaModalOpen] = useState(false);
    const [agendaMode, setAgendaMode] = useState("new");
    const [savingAgenda, setSavingAgenda] = useState(false);
    const [agendaDraft, setAgendaDraft] = useState({
        id: "",
        type: "seguimiento",
        channel: "whatsapp",
        subject: "",
        description: "",
        scheduled_at: "",
        next_action: "",
        next_action_date: "",
        status: "programada",
        priority: "media",
        result: "",
    });


    async function loadIndustries() {
        try {
            const data = await safeJson(`${API_BASE}/industries_list.php`, { headers: authHeaders() });
            setIndustries(Array.isArray(data) ? data : data.industries || data.rows || []);
        } catch {
            setIndustries([]);
        }
    }

    async function loadSections(industryId) {
        if (!industryId) {
            setSections([]);
            return;
        }

        try {
            const data = await safeJson(`${API_BASE}/industry_sections_list.php?industry_id=${industryId}`, {
                headers: authHeaders(),
            });
            setSections(Array.isArray(data) ? data : data.sections || data.rows || data.items || []);
        } catch {
            setSections([]);
        }
    }

    async function loadPmUsers() {
        if (!canAdmin) return;

        try {
            const data = await safeJson(`${API_BASE}/users_pm_list.php`, { headers: authHeaders() });
            setPmUsers(data.users || data.rows || []);
        } catch {
            setPmUsers([]);
        }
    }

    function openCompanyEdit() {
        if (!company) return;

        const csv = company.section_ids_csv || "";
        const ids = csv ? String(csv).split(",").filter(Boolean) : [];
        const currentSectionId = company.section_id || ids[0] || "";

        const draft = {
            id: company.id,
            name: company.name || "",
            status: company.status || "Prospecto",
            region: company.region || "",
            city: company.city || "",
            address: company.address || "",
            phone: company.phone || "",
            email: company.email || "",
            website: company.website || "",
            sector_text: company.sector_text || "",
            notes: company.notes || "",
            industry_id: company.industry_id ? String(company.industry_id) : "",
            section_id: currentSectionId ? String(currentSectionId) : "",
            pm_user_id: company.pm_user_id ? String(company.pm_user_id) : "",
        };

        setCompanyDraft(draft);
        setEditOpen(true);
        loadIndustries();
        loadPmUsers();
        if (draft.industry_id) loadSections(draft.industry_id);
    }

    async function saveCompanyData(e) {
        e.preventDefault();
        if (!companyDraft?.id || !canEdit) return;

        try {
            setSavingCompany(true);
            setError("");
            setToast("");

            const payload = {
                ...companyDraft,
                industry_id: companyDraft.industry_id ? Number(companyDraft.industry_id) : null,
                section_id: companyDraft.section_id ? Number(companyDraft.section_id) : null,
                pm_user_id: companyDraft.pm_user_id ? Number(companyDraft.pm_user_id) : null,
            };

            await safeJson(`${API_BASE}/affiliate_company_update.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload),
            });

            setToast("Datos de empresa actualizados.");
            setEditOpen(false);
            await load();
        } catch (e2) {
            setError(e2.message);
        } finally {
            setSavingCompany(false);
        }
    }

    async function load() {
        try {
            setLoading(true);
            setError("");
            const data = await safeJson(`${API_BASE}/affiliate_company_detail.php?id=${id}`, {
                headers: authHeaders(),
            });
            setCompany(data.company || null);
            setSummary(data.summary || null);
            setContacts(Array.isArray(data.contacts) ? data.contacts : []);
            setActivities(Array.isArray(data.activities) ? data.activities : []);
            setStructure(Array.isArray(data.structure) ? data.structure : []);
            setLegacyStructure(Array.isArray(data.legacy_structure) ? data.legacy_structure : []);
            setDocuments(Array.isArray(data.documents) ? data.documents : []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }

    async function loadMasterContacts() {
        try {
            setMasterContactsLoading(true);
            const data = await safeJson(`${API_BASE}/contacts_list.php`, {
                headers: authHeaders(),
            });
            setMasterContacts(Array.isArray(data) ? data : data.contacts || data.rows || data.items || []);
        } catch (e) {
            setMasterContacts([]);
            setError(e.message);
        } finally {
            setMasterContactsLoading(false);
        }
    }

    useEffect(() => {
        load();
        loadMasterContacts();
    }, [id]);

    function setTabAndUrl(next) {
        setTab(next);
        setSearchParams(next === "resumen" ? {} : { tab: next });
    }

    function resetContactForm() {
        setEditingLinkId(null);
        setContactForm({
            contact_id: "",
            is_primary: false,
            role_type: "contacto_clave",
            decision_level: "influenciador",
            position_title: "",
            area: "",
            notes: "",
        });
    }

    function startEditContactLink(link) {
        setEditingLinkId(link.id);
        setContactForm({
            contact_id: String(link.contact_id || ""),
            is_primary: Number(link.is_primary || 0) === 1,
            role_type: link.role_type || "contacto_clave",
            decision_level: link.decision_level || "influenciador",
            position_title: link.position_title || link.position || "",
            area: link.area || link.contact_area || "",
            notes: link.notes || "",
        });
        setTabAndUrl("contactos");
    }


    function emptyContactDraft() {
        return {
            id: "",
            link_id: "",
            first_name: "",
            last_name: "",
            full_name: "",
            email: "",
            phone: "",
            mobile: "",
            position: "",
            area: "",
            linkedin: "",
            relationship_status: "frio",
            status: "activo",
            notes: "",
            role_type: "contacto_clave",
            decision_level: "influenciador",
            position_title: "",
            link_area: "",
            link_notes: "",
            is_primary: false,
        };
    }

    function openNewContact() {
        setContactEditorMode("new");
        setContactDraft({
            ...emptyContactDraft(),
            position_title: contactForm.position_title || "",
            link_area: contactForm.area || "",
            role_type: contactForm.role_type || "contacto_clave",
            decision_level: contactForm.decision_level || "influenciador",
            is_primary: contactForm.is_primary || false,
        });
        setContactEditorOpen(true);
    }

    function openEditContact(contact) {
        setContactEditorMode("edit");
        setContactDraft({
            id: contact.contact_id || contact.id || "",
            link_id: contact.id || contact.link_id || "",
            first_name: contact.first_name || "",
            last_name: contact.last_name || "",
            full_name: fullName(contact),
            email: contact.email || "",
            phone: contact.phone || "",
            mobile: contact.mobile || "",
            position: contact.position || contact.position_title || "",
            area: contact.contact_area || contact.area || "",
            linkedin: contact.linkedin || "",
            relationship_status: contact.relationship_status || "frio",
            status: contact.status || "activo",
            notes: contact.profile_notes || contact.personal_notes || "",
            role_type: contact.role_type || "contacto_clave",
            decision_level: contact.decision_level || "influenciador",
            position_title: contact.position_title || contact.position || "",
            link_area: contact.area || contact.contact_area || "",
            link_notes: contact.notes || "",
            is_primary: Number(contact.is_primary || 0) === 1,
        });
        setContactEditorOpen(true);
    }

    async function saveContactMaster(e) {
        e.preventDefault();
        if (!canEdit) return setError("No tienes permiso para gestionar contactos.");
        if (!contactDraft.full_name?.trim() && !contactDraft.first_name?.trim()) {
            return setError("Captura el nombre del contacto.");
        }

        try {
            setSavingContactMaster(true);
            setError("");
            setToast("");

            const endpoint = contactEditorMode === "edit" ? "contact_update.php" : "contact_create.php";
            const payload = {
                ...contactDraft,
                id: contactDraft.id ? Number(contactDraft.id) : undefined,
                link_id: contactDraft.link_id ? Number(contactDraft.link_id) : undefined,
                affiliate_company_id: Number(id),
                is_primary: contactDraft.is_primary ? 1 : 0,
                link_area: contactDraft.link_area,
                link_notes: contactDraft.link_notes,
            };

            await safeJson(`${API_BASE}/${endpoint}`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload),
            });

            setToast(contactEditorMode === "edit" ? "Contacto actualizado." : "Contacto creado y vinculado.");
            setContactEditorOpen(false);
            setContactDraft(emptyContactDraft());
            await load();
            await loadMasterContacts();
            setTabAndUrl("contactos");
        } catch (e2) {
            setError(e2.message);
        } finally {
            setSavingContactMaster(false);
        }
    }

    async function saveContactLink(e) {
        e.preventDefault();
        if (!canEdit) return setError("No tienes permiso para gestionar contactos.");
        if (!editingLinkId && !contactForm.contact_id) return setError("Selecciona un contacto del catálogo.");

        try {
            setLinkingContact(true);
            setError("");
            setToast("");

            const endpoint = editingLinkId
                ? "affiliate_company_contact_update.php"
                : "affiliate_company_contact_attach.php";

            const payload = {
                id: editingLinkId,
                company_id: Number(id),
                contact_id: contactForm.contact_id ? Number(contactForm.contact_id) : undefined,
                is_primary: contactForm.is_primary ? 1 : 0,
                role_type: contactForm.role_type,
                decision_level: contactForm.decision_level,
                position_title: contactForm.position_title,
                area: contactForm.area,
                notes: contactForm.notes,
            };

            await safeJson(`${API_BASE}/${endpoint}`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload),
            });

            setToast(editingLinkId ? "Rol de contacto actualizado." : "Contacto vinculado correctamente.");
            resetContactForm();
            await load();
        } catch (e2) {
            setError(e2.message);
        } finally {
            setLinkingContact(false);
        }
    }

    async function removeContactLink(linkId) {
        if (!canEdit) return setError("No tienes permiso para desvincular contactos.");
        if (!window.confirm("¿Desvincular este contacto de la empresa?")) return;

        try {
            setLinkingContact(true);
            setError("");
            await safeJson(`${API_BASE}/affiliate_company_contact_detach.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ id: linkId }),
            });
            setToast("Contacto desvinculado.");
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setLinkingContact(false);
        }
    }

    async function saveDocument(e) {
        e.preventDefault();

        if (!canEdit) return setError("No tienes permiso para gestionar documentos.");
        if (!documentForm.title?.trim()) return setError("El título del documento es obligatorio.");
        if (documentForm.doc_type === "file" && !documentForm.file) return setError("Selecciona un archivo.");
        if (documentForm.doc_type === "link" && !documentForm.url?.trim()) return setError("Captura la liga del documento.");

        try {
            setUploadingDocument(true);
            setError("");
            setToast("");

            const fd = new FormData();
            fd.append("company_id", String(id));
            fd.append("document_category", documentForm.document_category);
            fd.append("title", documentForm.title);
            fd.append("description", documentForm.description || "");
            fd.append("doc_type", documentForm.doc_type);
            fd.append("url", documentForm.url || "");
            if (documentForm.file) fd.append("file", documentForm.file);

            await safeJson(`${API_BASE}/affiliate_company_document_upload.php`, {
                method: "POST",
                headers: authHeaders(),
                body: fd,
            });

            setToast("Documento guardado correctamente.");
            setDocumentForm({
                document_category: "Expediente institucional",
                title: "",
                description: "",
                doc_type: "file",
                url: "",
                file: null,
            });
            await load();
            setTabAndUrl("documentos");
        } catch (e2) {
            setError(e2.message);
        } finally {
            setUploadingDocument(false);
        }
    }

    async function deleteDocument(docId) {
        if (!canEdit) return setError("No tienes permiso para eliminar documentos.");
        if (!window.confirm("¿Eliminar este documento del expediente?")) return;

        try {
            setError("");
            await safeJson(`${API_BASE}/affiliate_company_document_delete.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ id: docId }),
            });
            setToast("Documento eliminado.");
            await load();
        } catch (e) {
            setError(e.message);
        }
    }

    async function saveActivity(e) {
        e.preventDefault();
        if (!canEdit) return setError("No tienes permiso para registrar actividades.");
        if (!activityForm.subject?.trim()) return setError("El asunto de la actividad es obligatorio.");

        try {
            setSavingActivity(true);
            setError("");
            setToast("");

            await safeJson(`${API_BASE}/affiliate_company_activity_create.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    company_id: Number(id),
                    ...activityForm,
                }),
            });

            setActivityForm({
                type: "seguimiento",
                channel: "whatsapp",
                subject: "",
                description: "",
                scheduled_at: "",
                next_action: "",
                next_action_date: "",
                status: "programada",
                priority: "media",
                result: "",
            });
            setToast("Actividad registrada correctamente.");
            await load();
            setTabAndUrl("actividades");
        } catch (e2) {
            setError(e2.message);
        } finally {
            setSavingActivity(false);
        }
    }


    function emptyAgendaDraft() {
        return {
            id: "",
            type: "seguimiento",
            channel: "whatsapp",
            subject: "",
            description: "",
            scheduled_at: "",
            next_action: "",
            next_action_date: "",
            status: "programada",
            priority: "media",
            result: "",
        };
    }

    function openNewAgenda() {
        setAgendaMode("new");
        setAgendaDraft(emptyAgendaDraft());
        setAgendaModalOpen(true);
        setTabAndUrl("agenda");
    }

    function openEditAgenda(item) {
        setAgendaMode("edit");
        setAgendaDraft({
            id: item.id || "",
            type: item.type || "seguimiento",
            channel: item.channel || "whatsapp",
            subject: item.subject || "",
            description: item.description || "",
            scheduled_at: item.scheduled_at ? String(item.scheduled_at).replace(" ", "T").slice(0, 16) : "",
            next_action: item.next_action || "",
            next_action_date: item.next_action_date ? String(item.next_action_date).replace(" ", "T").slice(0, 16) : "",
            status: item.status || "programada",
            priority: item.priority || "media",
            result: item.result || "",
        });
        setAgendaModalOpen(true);
        setTabAndUrl("agenda");
    }

    async function saveAgenda(e) {
        e.preventDefault();
        if (!canEdit) return setError("No tienes permiso para gestionar agenda.");
        if (!agendaDraft.subject?.trim()) return setError("El asunto de agenda es obligatorio.");

        try {
            setSavingAgenda(true);
            setError("");
            setToast("");

            const endpoint = agendaMode === "edit"
                ? "affiliate_company_activity_update.php"
                : "affiliate_company_activity_create.php";

            const payload = {
                ...agendaDraft,
                id: agendaDraft.id ? Number(agendaDraft.id) : undefined,
                company_id: Number(id),
                status: agendaDraft.status || "programada",
            };

            await safeJson(`${API_BASE}/${endpoint}`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload),
            });

            setToast(agendaMode === "edit" ? "Agenda actualizada." : "Agenda creada correctamente.");
            setAgendaModalOpen(false);
            setAgendaDraft(emptyAgendaDraft());
            await load();
            setTabAndUrl("agenda");
        } catch (e2) {
            setError(e2.message);
        } finally {
            setSavingAgenda(false);
        }
    }

    async function updateAgendaStatus(item, status) {
        if (!canEdit) return setError("No tienes permiso para actualizar agenda.");

        let result = item.result || "";

        if (status === "realizada") {
            const typed = window.prompt(
                "Resultado obligatorio de la acción realizada:",
                result || item.next_action || item.subject || ""
            );

            if (typed === null) return;

            result = String(typed || "").trim();

            if (!result) {
                setError("Para marcar una agenda como realizada debes capturar un resultado.");
                return;
            }
        }

        try {
            setError("");
            setToast("");

            await safeJson(`${API_BASE}/affiliate_company_activity_status_update.php`, {
                method: "POST",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    id: item.id,
                    status,
                    result,
                }),
            });

            setToast(status === "realizada" ? "Agenda marcada como realizada y reflejada en actividades." : "Estatus de agenda actualizado.");
            await load();
            setTabAndUrl("agenda");
        } catch (e2) {
            setError(e2.message);
        }
    }

    function reprogramAgenda(item) {
        openEditAgenda({
            ...item,
            status: "reprogramada",
        });
    }

    function createNextActionFromAgenda(item) {
        setAgendaMode("new");
        setAgendaDraft({
            ...emptyAgendaDraft(),
            type: item.type || "seguimiento",
            channel: item.channel || "whatsapp",
            priority: item.priority || "media",
            subject: item.next_action ? `Seguimiento: ${item.next_action}` : `Seguimiento: ${item.subject || "agenda"}`,
            description: item.result ? `Derivada de agenda realizada. Resultado anterior: ${item.result}` : `Derivada de agenda: ${item.subject || ""}`,
            status: "programada",
        });
        setAgendaModalOpen(true);
        setTabAndUrl("agenda");
    }

    const missing = summary?.missing || [];
    const tone = scoreTone(summary?.score_level);

    const agendaItems = useMemo(() => {
        return activities
            .filter((a) => a.scheduled_at || a.next_action_date || a.status === "programada" || a.status === "pendiente")
            .sort((a, b) => {
                const av = a.next_action_date || a.scheduled_at || a.created_at || "";
                const bv = b.next_action_date || b.scheduled_at || b.created_at || "";
                return String(av).localeCompare(String(bv));
            });
    }, [activities]);

    const overdueAgenda = useMemo(() => {
        const now = new Date();
        return agendaItems.filter((a) => {
            const raw = a.next_action_date || a.scheduled_at;
            if (!raw) return false;
            const d = new Date(String(raw).replace(" ", "T"));
            if (Number.isNaN(d.getTime())) return false;
            return d < now && !isAgendaClosed(a);
        });
    }, [agendaItems]);

    const todayAgenda = useMemo(() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = now.getMonth();
        const d = now.getDate();

        return agendaItems.filter((a) => {
            const raw = a.next_action_date || a.scheduled_at;
            if (!raw || isAgendaClosed(a)) return false;
            const date = new Date(String(raw).replace(" ", "T"));
            if (Number.isNaN(date.getTime())) return false;
            return date.getFullYear() === y && date.getMonth() === m && date.getDate() === d;
        });
    }, [agendaItems]);

    const pendingAgenda = useMemo(() => {
        return agendaItems.filter((a) => ["programada", "pendiente", "reprogramada"].includes(String(a.status || "").toLowerCase()));
    }, [agendaItems]);

    const completedAgenda = useMemo(() => {
        return agendaItems.filter((a) => String(a.status || "").toLowerCase() === "realizada");
    }, [agendaItems]);

    if (loading) {
        return (
            <MainLayout>
                <div className="p-4 text-sm text-[#9fc4df]">Cargando ficha 360 de empresa...</div>
            </MainLayout>
        );
    }

    if (error && !company) {
        return (
            <MainLayout>
                <div className="p-4">
                    <button onClick={() => navigate("/affiliate-companies")} className="text-xs text-cani-primary hover:underline">← Regresar</button>
                    <div className="mt-3 rounded-xl border border-[#ef4444]/50 bg-[#38161c] text-[#fca5a5] px-3 py-2 text-xs">{error}</div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <Style />
            <div className="min-h-screen bg-[#041426] px-3 py-6 md:px-6 md:py-8 space-y-6 affiliate-360">
                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                    <div>
                        <button onClick={() => navigate("/affiliate-companies")} className="text-[11px] text-cani-primary hover:underline mb-2">
                            ← Regresar a Cámaras y Organismos
                        </button>
                        <h2 className="text-lg md:text-xl font-semibold text-white">Ficha 360 Cámara / Organismo</h2>
                        <p className="text-xs md:text-sm text-[#9fc4df] max-w-4xl">
                            {company?.name} · {company?.region || "Sin región"} · {company?.city || "Sin ciudad"}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button onClick={load} className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-white text-xs hover:bg-[#d4af37]Light">
                            Recargar
                        </button>
                    </div>
                </div>

                {toast ? <div className="rounded-lg border border-[#10b981]/40 bg-[#0b3a36] text-[#7cebd0] px-3 py-2 text-xs">{toast}</div> : null}
                {error ? <div className="rounded-lg border border-[#ef4444]/50 bg-[#38161c] text-[#fca5a5] px-3 py-2 text-xs">{error}</div> : null}

                <div
                    className="rounded-2xl border p-4 shadow-sm"
                    style={{
                        background:
                            tone === "green"
                                ? "linear-gradient(135deg, rgba(5,78,64,.95), rgba(4,25,43,.98))"
                                : tone === "amber"
                                    ? "linear-gradient(135deg, rgba(83,61,12,.95), rgba(4,25,43,.98))"
                                    : "linear-gradient(135deg, rgba(86,25,31,.95), rgba(4,25,43,.98))",
                        borderColor:
                            tone === "green"
                                ? "#10b981"
                                : tone === "amber"
                                    ? "#d4af37"
                                    : "#ef4444",
                        color: "#f5fbff",
                    }}
                >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                        <div>
                            <div className="text-[11px] text-[#9fc4df]">Resumen de entidad</div>
                            <div className="text-sm font-semibold text-white">{company?.name || "Empresa"}</div>
                        </div>
                        {canEdit ? (
                            <button onClick={openCompanyEdit} className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-white text-xs hover:bg-[#d4af37]Light shadow-sm">
                                Editar datos de entidad
                            </button>
                        ) : null}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        <div>
                            <div className="text-[11px] text-[#9fc4df]">Score de afiliación</div>
                            <div className="text-3xl font-semibold text-white mt-1">{summary?.score || 0}/100</div>
                            <div className="mt-2 h-2 bg-[#04192b]/70 rounded-full overflow-hidden">
                                <div className="h-full rounded-full bg-[#22d3ee]" style={{ width: `${summary?.score || 0}%` }} />
                            </div>
                            <div className="mt-2"><Badge tone={tone}>{summary?.score_level || "sin lectura"}</Badge></div>
                        </div>

                        <div className="lg:col-span-2">
                            <div className="text-[11px] text-[#9fc4df]">Lectura ejecutiva</div>
                            <div className="text-sm font-semibold text-white mt-1">
                                {tone === "green"
                                    ? "La entidad tiene buena madurez de afiliación."
                                    : tone === "amber"
                                        ? "La entidad tiene avance, pero faltan elementos de seguimiento."
                                        : "La entidad requiere captura base para avanzar en afiliación."}
                            </div>
                            <p className="text-xs text-[#d7e8f6] mt-1">
                                El score considera contacto vinculado, actividad registrada, responsable, eje, documentos, datos de contacto y avance en el proceso.
                            </p>
                        </div>

                        <div>
                            <div className="text-[11px] text-[#9fc4df]">Faltantes</div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {missing.length === 0 ? <Badge tone="green">Sin faltantes críticos</Badge> : missing.slice(0, 6).map((m) => <Badge key={m} tone="red">{m}</Badge>)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
                    <Kpi title="Contactos" value={summary?.contacts_count || 0} subtitle={summary?.has_decisor ? "Decisor identificado" : "Vinculados"} tone={(summary?.contacts_count || 0) > 0 ? "green" : "red"} />
                    <Kpi title="Actividades" value={summary?.activities_count || 0} subtitle="Historial" tone={(summary?.activities_count || 0) > 0 ? "green" : "red"} />
                    <Kpi title="Documentos" value={summary?.documents_count || 0} subtitle="Expediente" tone={(summary?.documents_count || 0) > 0 ? "green" : "red"} />
                    <Kpi title="Estructura" value={summary?.structure_count || 0} subtitle="Afiliado" tone={(summary?.structure_count || 0) > 0 ? "blue" : "gray"} />
                    <Kpi title="Etapa" value={company?.status || "—"} subtitle="Proceso" tone="purple" />
                    <Kpi title="PM" value={company?.pm_iniciales || "—"} subtitle={company?.pm_display_name || "Responsable"} tone={company?.pm_user_id ? "green" : "amber"} />
                    <Kpi title="Agenda" value={agendaItems.length} subtitle={`${overdueAgenda.length} vencida(s)`} tone={overdueAgenda.length > 0 ? "red" : agendaItems.length > 0 ? "green" : "amber"} />
                </div>

                <div className="bg-[#04192b] border border-[#0b4d78] rounded-[24px] shadow-[0_12px_30px_rgba(0,0,0,.22)] p-2 flex flex-wrap gap-2">
                    {[
                        ["resumen", "Resumen"],
                        ["contactos", "Contactos"],
                        ["agenda", "Agenda"],
                        ["actividades", "Actividades"],
                        ["documentos", "Documentos"],
                        ["estructura", "Estructura"],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setTabAndUrl(key)}
                            className={`px-3 py-1.5 rounded-lg text-xs border ${tab === key ? "bg-[#d4af37] text-[#081a2b] border-[#d4af37]" : "bg-[#0b2740] text-[#f5fbff] border-[#24537a] hover:bg-[#10324f] hover:border-[#d4af37]"}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {tab === "resumen" ? (
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                        <Section title="Datos generales" subtitle="Información base de la empresa objetivo.">
                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div><div className="text-[#9fc4df]">Empresa</div><div className="font-semibold text-white">{company?.name || "—"}</div></div>
                                <div><div className="text-[#9fc4df]">Estatus</div><Badge tone="blue">{company?.status || "—"}</Badge></div>
                                <div><div className="text-[#9fc4df]">Región</div><div>{company?.region || "—"}</div></div>
                                <div><div className="text-[#9fc4df]">Ciudad</div><div>{company?.city || "—"}</div></div>
                                <div><div className="text-[#9fc4df]">Teléfono</div><div>{company?.phone || "—"}</div></div>
                                <div><div className="text-[#9fc4df]">Email</div><div>{company?.email || "—"}</div></div>
                                <div className="md:col-span-2"><div className="text-[#9fc4df]">Dirección</div><div>{company?.address || "—"}</div></div>
                                <div className="md:col-span-2"><div className="text-[#9fc4df]">Sitio web</div><div>{company?.website || "—"}</div></div>
                            </div>
                        </Section>

                        <Section title="Eje / sección" subtitle="Clasificación institucional de afiliación.">
                            <div className="p-4 space-y-3 text-xs">
                                <div><div className="text-[#9fc4df]">Eje / Industria</div><div className="font-semibold text-white">{displayIndustry(company)}</div></div>
                                <div><div className="text-[#9fc4df]">Sección</div><div>{company?.section_titles || "Sin sección vinculada"}</div></div>
                                <div><div className="text-[#9fc4df]">Descripción sección</div><div className="text-[#d7e8f6]">{company?.section_descriptions || "—"}</div></div>
                                <div><div className="text-[#9fc4df]">Rubro</div><div>{company?.sector_text || "—"}</div></div>
                            </div>
                        </Section>

                        <Section title="Contacto principal" subtitle="Primer contacto de referencia.">
                            <div className="p-4 text-xs">
                                {summary?.primary_contact ? (
                                    <div className="space-y-2">
                                        <div className="font-semibold text-white">{fullName(summary.primary_contact)}</div>
                                        <div>{summary.primary_contact.position_title || summary.primary_contact.position || "Sin puesto"}</div>
                                        <div>{summary.primary_contact.email || "Sin email"}</div>
                                        <div>{summary.primary_contact.mobile || summary.primary_contact.phone || "Sin teléfono"}</div>
                                        {Number(summary.primary_contact.is_primary || 0) === 1 ? <Badge tone="green">Principal</Badge> : null}
                                    </div>
                                ) : (
                                    <div className="text-[#9fc4df]">Sin contacto vinculado.</div>
                                )}
                            </div>
                        </Section>
                    </div>
                ) : null}

                {tab === "contactos" ? (
                    <div className="space-y-4">
                        <Section
                            title="Contactos de empresa"
                            subtitle="Crea contactos nuevos, vincula existentes y administra rol, nivel de decisión y contacto principal."
                            right={canEdit ? (
                                <button type="button" onClick={openNewContact} className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-white text-xs hover:bg-[#d4af37]Light">
                                    Nuevo contacto
                                </button>
                            ) : null}
                        >
                            <form onSubmit={saveContactLink} className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-xs">
                                <div className="field xl:col-span-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <label>Contacto del catálogo maestro</label>
                                        {canEdit ? (
                                            <button type="button" onClick={openNewContact} className="text-[11px] text-cani-primary hover:underline">
                                                + Registrar nuevo
                                            </button>
                                        ) : null}
                                    </div>
                                    <select
                                        value={contactForm.contact_id}
                                        onChange={(e) => setContactForm({ ...contactForm, contact_id: e.target.value })}
                                        disabled={!!editingLinkId || masterContactsLoading}
                                    >
                                        <option value="">
                                            {masterContactsLoading ? "Cargando contactos..." : "— Seleccionar contacto —"}
                                        </option>
                                        {masterContacts.map((c) => (
                                            <option key={c.id} value={String(c.id)}>
                                                {fullName(c)}{c.position ? ` · ${c.position}` : ""}{c.email ? ` · ${c.email}` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="field">
                                    <label>Rol de afiliación</label>
                                    <select value={contactForm.role_type} onChange={(e) => setContactForm({ ...contactForm, role_type: e.target.value })}>
                                        <option value="contacto_clave">Contacto clave</option>
                                        <option value="decisor">Decisor</option>
                                        <option value="sponsor">Sponsor</option>
                                        <option value="administrativo">Administrativo</option>
                                        <option value="operativo">Operativo</option>
                                        <option value="influenciador">Influenciador</option>
                                    </select>
                                </div>
                                <div className="field">
                                    <label>Nivel de decisión</label>
                                    <select value={contactForm.decision_level} onChange={(e) => setContactForm({ ...contactForm, decision_level: e.target.value })}>
                                        <option value="decisor_final">Decisor final</option>
                                        <option value="alto">Alto</option>
                                        <option value="medio">Medio</option>
                                        <option value="influenciador">Influenciador</option>
                                        <option value="operativo">Operativo</option>
                                        <option value="administrativo">Administrativo</option>
                                    </select>
                                </div>
                                <div className="field">
                                    <label>Puesto en esta empresa</label>
                                    <input value={contactForm.position_title} onChange={(e) => setContactForm({ ...contactForm, position_title: e.target.value })} placeholder="Ej. Director, Gerente, Administración" />
                                </div>
                                <div className="field">
                                    <label>Área</label>
                                    <input value={contactForm.area} onChange={(e) => setContactForm({ ...contactForm, area: e.target.value })} placeholder="Ej. Dirección, Finanzas, TI" />
                                </div>
                                <div className="field xl:col-span-2">
                                    <label>Notas del vínculo</label>
                                    <input value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} placeholder="Contexto, interés, influencia o pendiente." />
                                </div>
                                <label className="flex items-center gap-2 text-[11px] text-[#d9e8f5]">
                                    <input type="checkbox" checked={contactForm.is_primary} onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })} />
                                    Contacto principal
                                </label>
                                <div className="xl:col-span-4 flex justify-end gap-2">
                                    {editingLinkId ? (
                                        <button type="button" onClick={resetContactForm} className="px-3 py-1.5 rounded-lg border border-[#0b4d78] text-[#d9e8f5] text-xs hover:bg-[#10324f]">
                                            Cancelar edición
                                        </button>
                                    ) : null}
                                    <button disabled={linkingContact || !canEdit} className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-white text-xs hover:bg-[#d4af37]Light disabled:opacity-60">
                                        {linkingContact ? "Guardando..." : editingLinkId ? "Actualizar rol" : "Vincular contacto"}
                                    </button>
                                </div>
                            </form>
                        </Section>

                        <Section title="Contactos vinculados" subtitle="Contactos estratégicos dentro del proceso de afiliación.">
                            <div className="overflow-x-auto">
                                <table className="min-w-[1250px] w-full text-xs">
                                    <thead className="bg-cani-bg">
                                        <tr className="text-left text-[11px] text-[#d7e8f6] font-semibold">
                                            <th className="px-3 py-2">Contacto</th>
                                            <th className="px-3 py-2">Rol afiliación</th>
                                            <th className="px-3 py-2">Puesto / área</th>
                                            <th className="px-3 py-2">Nivel decisión</th>
                                            <th className="px-3 py-2">Email</th>
                                            <th className="px-3 py-2">Teléfono</th>
                                            <th className="px-3 py-2">Notas</th>
                                            <th className="px-3 py-2">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contacts.map((c) => (
                                            <tr key={c.id || c.contact_id} className="border-t border-[#0b4d78]">
                                                <td className="px-3 py-2">
                                                    <div className="font-semibold text-white">{fullName(c)}</div>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {Number(c.is_primary || 0) === 1 ? <Badge tone="green">Principal</Badge> : null}
                                                        {c.role_type === "decisor" || c.decision_level === "decisor_final" ? <Badge tone="purple">Decisor</Badge> : null}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2">{c.role_type || "contacto_clave"}</td>
                                                <td className="px-3 py-2">{c.position_title || c.position || "—"}<div className="text-[11px] text-[#9fc4df]">{c.area || c.contact_area || "—"}</div></td>
                                                <td className="px-3 py-2">{c.decision_level || "—"}</td>
                                                <td className="px-3 py-2">{c.email || "—"}</td>
                                                <td className="px-3 py-2">{c.mobile || c.phone || "—"}</td>
                                                <td className="px-3 py-2 text-[#d7e8f6]">{c.notes || "—"}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex flex-wrap gap-1">
                                                        {canEdit ? (
                                                            <>
                                                                <button type="button" onClick={() => openEditContact(c)} className="px-2 py-1 rounded-lg border border-[#0b4d78] hover:bg-[#10324f]">
                                                                    Editar contacto
                                                                </button>
                                                                <button type="button" onClick={() => startEditContactLink(c)} className="px-2 py-1 rounded-lg border border-[#0b4d78] hover:bg-[#10324f]">
                                                                    Rol/vínculo
                                                                </button>
                                                            </>
                                                        ) : null}
                                                        {canEdit ? (
                                                            <button type="button" onClick={() => removeContactLink(c.id)} className="px-2 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50">
                                                                Quitar
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {contacts.length === 0 ? <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">Sin contactos vinculados.</td></tr> : null}
                                    </tbody>
                                </table>
                            </div>
                        </Section>
                    </div>
                ) : null}

                {tab === "agenda" ? (
                    <Section
                        title="Agenda / próximas acciones"
                        subtitle="Pendientes futuros, seguimientos programados, vencidos y acciones realizadas."
                        right={canEdit ? (
                            <button type="button" onClick={openNewAgenda} className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-white text-xs hover:bg-[#d4af37]Light">
                                Nueva agenda
                            </button>
                        ) : null}
                    >
                        <div className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
                            <Kpi title="Agenda total" value={agendaItems.length} subtitle="Acciones vinculadas a esta empresa" tone="blue" />
                            <Kpi title="Vencidas" value={overdueAgenda.length} subtitle="Requieren atención" tone={overdueAgenda.length ? "red" : "green"} />
                            <Kpi title="Hoy" value={todayAgenda.length} subtitle="Acciones del día" tone="amber" />
                            <Kpi title="Pendientes" value={pendingAgenda.length} subtitle="Por ejecutar" tone="blue" />
                            <Kpi title="Realizadas" value={completedAgenda.length} subtitle="Ya cerradas" tone="green" />
                        </div>

                        <div className="overflow-x-auto border-t border-[#0b4d78]">
                            <table className="min-w-[1320px] w-full text-xs">
                                <thead className="bg-cani-bg">
                                    <tr className="text-left text-[11px] text-[#d7e8f6] font-semibold">
                                        <th className="px-3 py-2">Fecha seguimiento</th>
                                        <th className="px-3 py-2">Actividad</th>
                                        <th className="px-3 py-2">Próxima acción</th>
                                        <th className="px-3 py-2">Estatus</th>
                                        <th className="px-3 py-2">Prioridad</th>
                                        <th className="px-3 py-2">Resultado</th>
                                        <th className="px-3 py-2">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {agendaItems.map((a) => {
                                        const agendaDate = a.next_action_date || a.scheduled_at || "";
                                        const isDone = String(a.status || "").toLowerCase() === "realizada";
                                        const isCancelled = String(a.status || "").toLowerCase() === "cancelada";
                                        const isOverdue = overdueAgenda.some((x) => String(x.id) === String(a.id));

                                        return (
                                            <tr key={a.id} className={`border-t border-[#0b4d78] ${isOverdue ? "bg-red-50/60" : ""}`}>
                                                <td className="px-3 py-2">
                                                    <div className="font-semibold text-white">{formatHumanDate(agendaDate)}</div>
                                                    <div className="text-[11px] text-[#9fc4df]">Creada: {formatHumanDate(a.created_at)}</div>
                                                    {isOverdue ? <div className="mt-1"><Badge tone="red">Vencida</Badge></div> : null}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="font-semibold">{a.subject || "—"}</div>
                                                    <div className="text-[11px] text-[#9fc4df]">{a.type || "—"} · {a.channel || "—"}</div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div>{a.next_action || "—"}</div>
                                                    {a.description ? <div className="text-[11px] text-[#9fc4df] mt-1">{a.description}</div> : null}
                                                </td>
                                                <td className="px-3 py-2"><Badge tone={isDone ? "green" : isCancelled ? "red" : "blue"}>{a.status || "—"}</Badge></td>
                                                <td className="px-3 py-2"><Badge tone={a.priority === "critica" || a.priority === "alta" ? "red" : a.priority === "media" ? "amber" : "gray"}>{a.priority || "—"}</Badge></td>
                                                <td className="px-3 py-2 text-[#d7e8f6]">{a.result || "—"}</td>
                                                <td className="px-3 py-2">
                                                    {canEdit ? (
                                                        <div className="flex flex-wrap gap-1">
                                                            <button type="button" onClick={() => openEditAgenda(a)} className="px-2 py-1 rounded-lg border border-[#0b4d78] hover:bg-[#10324f]">
                                                                Editar
                                                            </button>
                                                            {!isDone && !isCancelled ? (
                                                                <button type="button" onClick={() => updateAgendaStatus(a, "realizada")} className="px-2 py-1 rounded-lg border border-green-200 text-green-700 hover:bg-green-50">
                                                                    Realizada
                                                                </button>
                                                            ) : null}
                                                            {!isDone && !isCancelled ? (
                                                                <button type="button" onClick={() => reprogramAgenda(a)} className="px-2 py-1 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50">
                                                                    Reprogramar
                                                                </button>
                                                            ) : null}
                                                            {isDone ? (
                                                                <button type="button" onClick={() => createNextActionFromAgenda(a)} className="px-2 py-1 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50">
                                                                    Siguiente acción
                                                                </button>
                                                            ) : null}
                                                            {!isCancelled && !isDone ? (
                                                                <button type="button" onClick={() => updateAgendaStatus(a, "cancelada")} className="px-2 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50">
                                                                    Cancelar
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    ) : "—"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {agendaItems.length === 0 ? <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">Sin agenda programada.</td></tr> : null}
                                </tbody>
                            </table>
                        </div>
                    </Section>
                ) : null}

                {tab === "actividades" ? (
                    <div className="space-y-4">
                        <Section title="Registrar actividad / agenda" subtitle="Seguimiento con fecha programada, próxima acción, prioridad y resultado.">
                            <form onSubmit={saveActivity} className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-xs">
                                <div className="field">
                                    <label>Tipo</label>
                                    <select value={activityForm.type} onChange={(e) => setActivityForm({ ...activityForm, type: e.target.value })}>
                                        <option value="seguimiento">Seguimiento</option>
                                        <option value="email_enviado">Email enviado</option>
                                        <option value="llamada">Llamada</option>
                                        <option value="whatsapp">WhatsApp</option>
                                        <option value="reunion">Reunión</option>
                                        <option value="documentos">Documentos</option>
                                    </select>
                                </div>
                                <div className="field">
                                    <label>Canal</label>
                                    <select value={activityForm.channel} onChange={(e) => setActivityForm({ ...activityForm, channel: e.target.value })}>
                                        <option value="whatsapp">WhatsApp</option>
                                        <option value="email">Email</option>
                                        <option value="telefono">Teléfono</option>
                                        <option value="presencial">Presencial</option>
                                        <option value="videollamada">Videollamada</option>
                                    </select>
                                </div>
                                <div className="field">
                                    <label>Fecha programada</label>
                                    <input type="datetime-local" value={activityForm.scheduled_at} onChange={(e) => setActivityForm({ ...activityForm, scheduled_at: e.target.value })} />
                                </div>
                                <div className="field">
                                    <label>Estatus</label>
                                    <select value={activityForm.status} onChange={(e) => setActivityForm({ ...activityForm, status: e.target.value })}>
                                        <option value="programada">Programada</option>
                                        <option value="pendiente">Pendiente</option>
                                        <option value="realizada">Realizada</option>
                                        <option value="reprogramada">Reprogramada</option>
                                        <option value="cancelada">Cancelada</option>
                                    </select>
                                </div>
                                <div className="field md:col-span-2">
                                    <label>Asunto</label>
                                    <input value={activityForm.subject} onChange={(e) => setActivityForm({ ...activityForm, subject: e.target.value })} placeholder="Ej. Solicitud de documentos institucionales" />
                                </div>
                                <div className="field">
                                    <label>Prioridad</label>
                                    <select value={activityForm.priority} onChange={(e) => setActivityForm({ ...activityForm, priority: e.target.value })}>
                                        <option value="baja">Baja</option>
                                        <option value="media">Media</option>
                                        <option value="alta">Alta</option>
                                        <option value="critica">Crítica</option>
                                    </select>
                                </div>
                                <div className="field">
                                    <label>Fecha próxima acción</label>
                                    <input type="datetime-local" value={activityForm.next_action_date} onChange={(e) => setActivityForm({ ...activityForm, next_action_date: e.target.value })} />
                                </div>
                                <div className="field md:col-span-2">
                                    <label>Próxima acción</label>
                                    <input value={activityForm.next_action} onChange={(e) => setActivityForm({ ...activityForm, next_action: e.target.value })} placeholder="Ej. Enviar formato, llamar, solicitar pago..." />
                                </div>
                                <div className="field md:col-span-2">
                                    <label>Resultado</label>
                                    <input value={activityForm.result} onChange={(e) => setActivityForm({ ...activityForm, result: e.target.value })} placeholder="Ej. Aceptó enviar documentos esta semana" />
                                </div>
                                <div className="field md:col-span-4">
                                    <label>Descripción</label>
                                    <textarea rows={3} value={activityForm.description} onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })} placeholder="Qué se hizo, respuesta de la empresa y contexto relevante." />
                                </div>
                                <div className="md:col-span-4 flex justify-end">
                                    <button disabled={savingActivity || !canEdit} className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-white text-xs hover:bg-[#d4af37]Light disabled:opacity-60">
                                        {savingActivity ? "Guardando..." : "Guardar actividad / agenda"}
                                    </button>
                                </div>
                            </form>
                        </Section>

                        <Section title="Historial de actividades" subtitle="Últimos seguimientos registrados.">
                            <div className="overflow-x-auto">
                                <table className="min-w-[1100px] w-full text-xs">
                                    <thead className="bg-cani-bg">
                                        <tr className="text-left text-[11px] text-[#d7e8f6] font-semibold">
                                            <th className="px-3 py-2">Fecha / agenda</th>
                                            <th className="px-3 py-2">Tipo / canal</th>
                                            <th className="px-3 py-2">Asunto</th>
                                            <th className="px-3 py-2">Próxima acción</th>
                                            <th className="px-3 py-2">Estatus</th>
                                            <th className="px-3 py-2">Prioridad</th>
                                            <th className="px-3 py-2">Usuario</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activities.map((a) => (
                                            <tr key={a.id} className="border-t border-[#0b4d78]">
                                                <td className="px-3 py-2">
                                                    <div>{formatHumanDate(a.created_at)}</div>
                                                    <div className="text-[11px] text-[#9fc4df]">Prog: {formatHumanDate(a.scheduled_at)}</div>
                                                </td>
                                                <td className="px-3 py-2"><strong>{a.type || "—"}</strong><div className="text-[11px] text-[#9fc4df]">{a.channel || "—"}</div></td>
                                                <td className="px-3 py-2">{a.subject || "—"}<div className="text-[11px] text-[#9fc4df]">{a.result || a.description || ""}</div></td>
                                                <td className="px-3 py-2">{a.next_action || "—"}<div className="text-[11px] text-[#9fc4df]">{formatHumanDate(a.next_action_date, "")}</div></td>
                                                <td className="px-3 py-2"><Badge tone={a.status === "realizada" ? "green" : a.status === "cancelada" ? "red" : "blue"}>{a.status || "—"}</Badge></td>
                                                <td className="px-3 py-2">{a.priority || "—"}</td>
                                                <td className="px-3 py-2">{a.user_name || a.user_initials || "—"}</td>
                                            </tr>
                                        ))}
                                        {activities.length === 0 ? <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">Sin actividades registradas.</td></tr> : null}
                                    </tbody>
                                </table>
                            </div>
                        </Section>
                    </div>
                ) : null}


                {tab === "documentos" ? (
                    <div className="space-y-4">
                        <Section title="Expediente de afiliación" subtitle="Carga expediente institucional, formato de afiliación, comprobante, convenio y evidencias por entidad.">
                            <form onSubmit={saveDocument} className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-xs">
                                <div className="field">
                                    <label>Categoría</label>
                                    <select value={documentForm.document_category} onChange={(e) => setDocumentForm({ ...documentForm, document_category: e.target.value })}>
                                        <option value="Expediente institucional">Expediente institucional</option>
                                        <option value="Formato de afiliación">Formato de afiliación</option>
                                        <option value="Comprobante de pago">Comprobante de pago</option>
                                        <option value="Convenio">Convenio</option>
                                        <option value="INE / identificación">INE / identificación</option>
                                        <option value="Datos fiscales">Datos fiscales</option>
                                        <option value="Evidencia">Evidencia</option>
                                        <option value="Otro">Otro</option>
                                    </select>
                                </div>
                                <div className="field">
                                    <label>Tipo</label>
                                    <select value={documentForm.doc_type} onChange={(e) => setDocumentForm({ ...documentForm, doc_type: e.target.value })}>
                                        <option value="file">Archivo</option>
                                        <option value="link">Liga</option>
                                    </select>
                                </div>
                                <div className="field xl:col-span-2">
                                    <label>Título</label>
                                    <input value={documentForm.title} onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })} placeholder="Ej. Expediente institucional / Comprobante de pago" />
                                </div>

                                {documentForm.doc_type === "file" ? (
                                    <div className="field xl:col-span-2">
                                        <label>Archivo</label>
                                        <input type="file" onChange={(e) => setDocumentForm({ ...documentForm, file: e.target.files?.[0] || null })} />
                                    </div>
                                ) : (
                                    <div className="field xl:col-span-2">
                                        <label>URL</label>
                                        <input value={documentForm.url} onChange={(e) => setDocumentForm({ ...documentForm, url: e.target.value })} placeholder="https://..." />
                                    </div>
                                )}

                                <div className="field xl:col-span-2">
                                    <label>Descripción</label>
                                    <input value={documentForm.description} onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })} placeholder="Notas del documento o contexto del expediente." />
                                </div>

                                <div className="xl:col-span-4 flex justify-end">
                                    <button disabled={uploadingDocument || !canEdit} className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-white text-xs hover:bg-[#d4af37]Light disabled:opacity-60">
                                        {uploadingDocument ? "Subiendo..." : "Guardar documento"}
                                    </button>
                                </div>
                            </form>
                        </Section>

                        <Section title="Documentos registrados" subtitle="Expediente documental de afiliación de esta empresa.">
                            <div className="overflow-x-auto">
                                <table className="min-w-[1150px] w-full text-xs">
                                    <thead className="bg-cani-bg">
                                        <tr className="text-left text-[11px] text-[#d7e8f6] font-semibold">
                                            <th className="px-3 py-2">Categoría</th>
                                            <th className="px-3 py-2">Documento</th>
                                            <th className="px-3 py-2">Tipo</th>
                                            <th className="px-3 py-2">Archivo / liga</th>
                                            <th className="px-3 py-2">Subido por</th>
                                            <th className="px-3 py-2">Fecha</th>
                                            <th className="px-3 py-2">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {documents.map((d) => (
                                            <tr key={d.id} className="border-t border-[#0b4d78]">
                                                <td className="px-3 py-2"><Badge tone="purple">{d.document_category || "Otro"}</Badge></td>
                                                <td className="px-3 py-2">
                                                    <div className="font-semibold text-white">{d.title || "—"}</div>
                                                    <div className="text-[11px] text-[#9fc4df]">{d.description || "—"}</div>
                                                </td>
                                                <td className="px-3 py-2">{d.doc_type || "—"}</td>
                                                <td className="px-3 py-2">
                                                    {d.doc_type === "link" && d.url ? (
                                                        <a className="text-cani-primary hover:underline" href={d.url} target="_blank" rel="noreferrer">Abrir liga</a>
                                                    ) : d.file_path ? (
                                                        <a className="text-cani-primary hover:underline" href={d.file_path} target="_blank" rel="noreferrer">{d.file_name || "Abrir archivo"}</a>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-3 py-2">{d.uploaded_by_name || d.uploaded_by_initials || "—"}</td>
                                                <td className="px-3 py-2">{d.created_at || "—"}</td>
                                                <td className="px-3 py-2">
                                                    {canEdit ? (
                                                        <button type="button" onClick={() => deleteDocument(d.id)} className="px-2 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50">
                                                            Eliminar
                                                        </button>
                                                    ) : null}
                                                </td>
                                            </tr>
                                        ))}
                                        {documents.length === 0 ? <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">Sin documentos registrados.</td></tr> : null}
                                    </tbody>
                                </table>
                            </div>
                        </Section>
                    </div>
                ) : null}

                {tab === "estructura" ? (
                    <div className="space-y-4">
                        <Section
                            title="Estructura institucional vinculada"
                            subtitle="Consulta oficial de roles administrados desde Institución / Estructura CANIETI."
                            right={<Badge tone="blue">{structure.length} rol(es)</Badge>}
                        >
                            <div className="p-4">
                                <div className="rounded-xl border border-blue-100 bg-blue-50 text-blue-700 px-3 py-2 text-xs mb-3">
                                    Esta información se administra desde <strong>Institución / Estructura CANIETI</strong>.
                                    La Ficha 360 solo consulta los roles vinculados a esta empresa afiliada.
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-[1250px] w-full text-xs">
                                    <thead className="bg-cani-bg">
                                        <tr className="text-left text-[11px] text-[#d7e8f6] font-semibold">
                                            <th className="px-3 py-2">Rol institucional</th>
                                            <th className="px-3 py-2">Contacto</th>
                                            <th className="px-3 py-2">Grupo / tipo</th>
                                            <th className="px-3 py-2">Comité / área</th>
                                            <th className="px-3 py-2">Periodo</th>
                                            <th className="px-3 py-2">Estatus</th>
                                            <th className="px-3 py-2">Consejo</th>
                                            <th className="px-3 py-2">Assets</th>
                                            <th className="px-3 py-2">Notas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {structure.map((s) => {
                                            const contactName = s.contact_full_name || [s.contact_first_name, s.contact_last_name].filter(Boolean).join(" ").trim() || s.person_name || "—";
                                            const contactEmail = s.contact_email || s.email || "";
                                            const contactPhone = s.contact_mobile || s.contact_phone || s.phone || "";

                                            return (
                                                <tr key={s.id} className="border-t border-[#0b4d78]">
                                                    <td className="px-3 py-2">
                                                        <div className="font-semibold text-white">{s.role_title || "—"}</div>
                                                        <div className="text-[11px] text-[#9fc4df]">{s.organization || s.affiliate_company_name || company?.name || "—"}</div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="font-semibold">{contactName}</div>
                                                        <div className="text-[11px] text-[#9fc4df]">{contactEmail || "Sin email"}</div>
                                                        <div className="text-[11px] text-[#9fc4df]">{contactPhone || "Sin teléfono"}</div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div>{s.role_group || "—"}</div>
                                                        <div className="text-[11px] text-[#9fc4df]">{s.role_type || "—"}</div>
                                                    </td>
                                                    <td className="px-3 py-2">{s.committee_area || "—"}</td>
                                                    <td className="px-3 py-2">
                                                        <div>{s.period_start || "—"}</div>
                                                        <div className="text-[11px] text-[#9fc4df]">a {s.period_end || "—"}</div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <Badge tone={s.status === "activo" ? "green" : s.status === "suplente" ? "amber" : "gray"}>
                                                            {s.status || "—"}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {Number(s.is_board_member || 0) === 1 ? <Badge tone="purple">Sí</Badge> : <Badge tone="gray">No</Badge>}
                                                    </td>
                                                    <td className="px-3 py-2">{Number(s.assets_count || 0)}</td>
                                                    <td className="px-3 py-2 text-[#d7e8f6]">{s.notes || "—"}</td>
                                                </tr>
                                            );
                                        })}

                                        {structure.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} className="px-3 py-6 text-center text-gray-400">
                                                    Sin estructura institucional vinculada desde Institución / Estructura CANIETI.
                                                </td>
                                            </tr>
                                        ) : null}
                                    </tbody>
                                </table>
                            </div>
                        </Section>

                        <Section
                            title="Estructura heredada de afiliaciones"
                            subtitle="Consulta secundaria de registros antiguos en affiliate_company_structure."
                            right={<Badge tone="gray">{legacyStructure.length} registro(s)</Badge>}
                        >
                            <div className="p-4">
                                <div className="rounded-xl border border-amber-100 bg-amber-50 text-amber-700 px-3 py-2 text-xs mb-3">
                                    Estos registros son heredados. La administración formal ahora debe hacerse en <strong>Institución / Estructura CANIETI</strong>.
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-[900px] w-full text-xs">
                                    <thead className="bg-cani-bg">
                                        <tr className="text-left text-[11px] text-[#d7e8f6] font-semibold">
                                            <th className="px-3 py-2">Rol</th>
                                            <th className="px-3 py-2">Nombre</th>
                                            <th className="px-3 py-2">Email</th>
                                            <th className="px-3 py-2">Teléfono</th>
                                            <th className="px-3 py-2">Notas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {legacyStructure.map((s) => (
                                            <tr key={s.id} className="border-t border-[#0b4d78]">
                                                <td className="px-3 py-2 font-semibold text-white">{s.role_title || "—"}</td>
                                                <td className="px-3 py-2">{s.person_name || "—"}</td>
                                                <td className="px-3 py-2">{s.email || "—"}</td>
                                                <td className="px-3 py-2">{s.phone || "—"}</td>
                                                <td className="px-3 py-2 text-[#d7e8f6]">{s.notes || "—"}</td>
                                            </tr>
                                        ))}
                                        {legacyStructure.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="px-3 py-6 text-center text-gray-400">
                                                    Sin estructura heredada.
                                                </td>
                                            </tr>
                                        ) : null}
                                    </tbody>
                                </table>
                            </div>
                        </Section>
                    </div>
                ) : null}




                {agendaModalOpen ? (
                    <>
                        <div
                            onClick={() => setAgendaModalOpen(false)}
                            style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.45)", zIndex: 2150 }}
                        />
                        <div
                            style={{
                                position: "fixed",
                                inset: 0,
                                zIndex: 2250,
                                display: "grid",
                                placeItems: "center",
                                padding: 16,
                            }}
                        >
                            <form
                                onSubmit={saveAgenda}
                                className="bg-[#04192b] border border-[#0b4d78] rounded-2xl shadow-2xl"
                                style={{ width: "min(980px, 96vw)", maxHeight: "92vh", overflow: "auto" }}
                            >
                                <div className="px-4 py-3 border-b border-[#0b4d78] flex items-center justify-between gap-3">
                                    <div>
                                        <h3 className="text-sm font-semibold text-white">
                                            {agendaMode === "edit" ? "Editar agenda" : "Nueva agenda"}
                                        </h3>
                                        <p className="text-[11px] text-[#9fc4df]">
                                            Programa una próxima acción para esta empresa. Al marcarla como realizada quedará reflejada en actividades.
                                        </p>
                                    </div>
                                    <button type="button" onClick={() => setAgendaModalOpen(false)} className="px-3 py-1.5 rounded-lg bg-[#04192b] border border-[#0b4d78] text-xs">
                                        Cerrar
                                    </button>
                                </div>

                                <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-xs">
                                    <div className="field">
                                        <label>Tipo</label>
                                        <select value={agendaDraft.type} onChange={(e) => setAgendaDraft({ ...agendaDraft, type: e.target.value })}>
                                            <option value="seguimiento">Seguimiento</option>
                                            <option value="llamada">Llamada</option>
                                            <option value="whatsapp">WhatsApp</option>
                                            <option value="email_enviado">Email</option>
                                            <option value="reunion">Reunión</option>
                                            <option value="documentos">Documentos</option>
                                        </select>
                                    </div>

                                    <div className="field">
                                        <label>Canal</label>
                                        <select value={agendaDraft.channel} onChange={(e) => setAgendaDraft({ ...agendaDraft, channel: e.target.value })}>
                                            <option value="whatsapp">WhatsApp</option>
                                            <option value="email">Email</option>
                                            <option value="telefono">Teléfono</option>
                                            <option value="presencial">Presencial</option>
                                            <option value="videollamada">Videollamada</option>
                                        </select>
                                    </div>

                                    <div className="field">
                                        <label>Fecha programada</label>
                                        <input type="datetime-local" value={agendaDraft.scheduled_at} onChange={(e) => setAgendaDraft({ ...agendaDraft, scheduled_at: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Estatus</label>
                                        <select value={agendaDraft.status} onChange={(e) => setAgendaDraft({ ...agendaDraft, status: e.target.value })}>
                                            <option value="programada">Programada</option>
                                            <option value="pendiente">Pendiente</option>
                                            <option value="reprogramada">Reprogramada</option>
                                            <option value="realizada">Realizada</option>
                                            <option value="cancelada">Cancelada</option>
                                        </select>
                                    </div>

                                    <div className="field md:col-span-2">
                                        <label>Asunto</label>
                                        <input value={agendaDraft.subject} onChange={(e) => setAgendaDraft({ ...agendaDraft, subject: e.target.value })} placeholder="Ej. Llamar para seguimiento de documentos" />
                                    </div>

                                    <div className="field">
                                        <label>Prioridad</label>
                                        <select value={agendaDraft.priority} onChange={(e) => setAgendaDraft({ ...agendaDraft, priority: e.target.value })}>
                                            <option value="baja">Baja</option>
                                            <option value="media">Media</option>
                                            <option value="alta">Alta</option>
                                            <option value="critica">Crítica</option>
                                        </select>
                                    </div>

                                    <div className="field">
                                        <label>Fecha próxima acción</label>
                                        <input type="datetime-local" value={agendaDraft.next_action_date} onChange={(e) => setAgendaDraft({ ...agendaDraft, next_action_date: e.target.value })} />
                                    </div>

                                    <div className="field md:col-span-2">
                                        <label>Próxima acción</label>
                                        <input value={agendaDraft.next_action} onChange={(e) => setAgendaDraft({ ...agendaDraft, next_action: e.target.value })} placeholder="Ej. Enviar formato, llamar, solicitar pago..." />
                                    </div>

                                    <div className="field md:col-span-2">
                                        <label>Resultado</label>
                                        <input value={agendaDraft.result} onChange={(e) => setAgendaDraft({ ...agendaDraft, result: e.target.value })} placeholder="Se llena al cerrar o si ya hubo resultado." />
                                    </div>

                                    <div className="field md:col-span-4">
                                        <label>Descripción / contexto</label>
                                        <textarea rows={3} value={agendaDraft.description} onChange={(e) => setAgendaDraft({ ...agendaDraft, description: e.target.value })} placeholder="Contexto de la próxima acción y notas operativas." />
                                    </div>
                                </div>

                                <div className="px-4 py-3 border-t border-[#0b4d78] flex flex-wrap justify-end gap-2">
                                    <button type="button" onClick={() => setAgendaModalOpen(false)} className="px-3 py-1.5 rounded-lg bg-[#04192b] border border-[#0b4d78] text-xs">
                                        Cancelar
                                    </button>
                                    <button disabled={savingAgenda} type="submit" className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-white text-xs hover:bg-[#d4af37]Light">
                                        {savingAgenda ? "Guardando..." : agendaMode === "edit" ? "Guardar agenda" : "Crear agenda"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </>
                ) : null}

                {contactEditorOpen ? (
                    <>
                        <div
                            onClick={() => setContactEditorOpen(false)}
                            style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.45)", zIndex: 2200 }}
                        />
                        <div
                            style={{
                                position: "fixed",
                                inset: 0,
                                zIndex: 2300,
                                display: "grid",
                                placeItems: "center",
                                padding: 16,
                            }}
                        >
                            <form
                                onSubmit={saveContactMaster}
                                className="bg-[#04192b] border border-[#0b4d78] rounded-2xl shadow-2xl"
                                style={{ width: "min(1080px, 96vw)", maxHeight: "92vh", overflow: "auto" }}
                            >
                                <div className="px-4 py-3 border-b border-[#0b4d78] flex items-center justify-between gap-3">
                                    <div>
                                        <h3 className="text-sm font-semibold text-white">
                                            {contactEditorMode === "edit" ? "Editar contacto" : "Nuevo contacto"}
                                        </h3>
                                        <p className="text-[11px] text-[#9fc4df]">
                                            Datos del contacto y su rol dentro de esta empresa.
                                        </p>
                                    </div>
                                    <button type="button" onClick={() => setContactEditorOpen(false)} className="px-3 py-1.5 rounded-lg bg-[#04192b] border border-[#0b4d78] text-xs">
                                        Cerrar
                                    </button>
                                </div>

                                <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 text-xs">
                                    <div className="field">
                                        <label>Nombre</label>
                                        <input value={contactDraft.first_name} onChange={(e) => setContactDraft({ ...contactDraft, first_name: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Apellido</label>
                                        <input value={contactDraft.last_name} onChange={(e) => setContactDraft({ ...contactDraft, last_name: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Nombre completo</label>
                                        <input value={contactDraft.full_name} onChange={(e) => setContactDraft({ ...contactDraft, full_name: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Puesto general</label>
                                        <input value={contactDraft.position} onChange={(e) => setContactDraft({ ...contactDraft, position: e.target.value, position_title: contactDraft.position_title || e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Área</label>
                                        <input value={contactDraft.area} onChange={(e) => setContactDraft({ ...contactDraft, area: e.target.value, link_area: contactDraft.link_area || e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Relación</label>
                                        <select value={contactDraft.relationship_status} onChange={(e) => setContactDraft({ ...contactDraft, relationship_status: e.target.value })}>
                                            <option value="frio">Frío</option>
                                            <option value="conocido">Conocido</option>
                                            <option value="aliado">Aliado</option>
                                            <option value="sponsor_potencial">Sponsor potencial</option>
                                            <option value="sponsor_real">Sponsor real</option>
                                        </select>
                                    </div>

                                    <div className="field">
                                        <label>Email</label>
                                        <input value={contactDraft.email} onChange={(e) => setContactDraft({ ...contactDraft, email: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Teléfono</label>
                                        <input value={contactDraft.phone} onChange={(e) => setContactDraft({ ...contactDraft, phone: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Celular</label>
                                        <input value={contactDraft.mobile} onChange={(e) => setContactDraft({ ...contactDraft, mobile: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>LinkedIn</label>
                                        <input value={contactDraft.linkedin} onChange={(e) => setContactDraft({ ...contactDraft, linkedin: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Rol en afiliación</label>
                                        <select value={contactDraft.role_type} onChange={(e) => setContactDraft({ ...contactDraft, role_type: e.target.value })}>
                                            <option value="contacto_clave">Contacto clave</option>
                                            <option value="decisor">Decisor</option>
                                            <option value="sponsor">Sponsor</option>
                                            <option value="administrativo">Administrativo</option>
                                            <option value="operativo">Operativo</option>
                                            <option value="influenciador">Influenciador</option>
                                        </select>
                                    </div>

                                    <div className="field">
                                        <label>Nivel de decisión</label>
                                        <select value={contactDraft.decision_level} onChange={(e) => setContactDraft({ ...contactDraft, decision_level: e.target.value })}>
                                            <option value="decisor_final">Decisor final</option>
                                            <option value="alto">Alto</option>
                                            <option value="medio">Medio</option>
                                            <option value="influenciador">Influenciador</option>
                                            <option value="operativo">Operativo</option>
                                            <option value="administrativo">Administrativo</option>
                                        </select>
                                    </div>

                                    <div className="field">
                                        <label>Puesto en esta empresa</label>
                                        <input value={contactDraft.position_title} onChange={(e) => setContactDraft({ ...contactDraft, position_title: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Área en esta empresa</label>
                                        <input value={contactDraft.link_area} onChange={(e) => setContactDraft({ ...contactDraft, link_area: e.target.value })} />
                                    </div>

                                    <label className="flex items-center gap-2 text-[11px] text-[#d9e8f5] mt-6">
                                        <input type="checkbox" checked={contactDraft.is_primary} onChange={(e) => setContactDraft({ ...contactDraft, is_primary: e.target.checked })} />
                                        Contacto principal
                                    </label>

                                    <div className="field xl:col-span-3">
                                        <label>Notas del contacto</label>
                                        <textarea rows={2} value={contactDraft.notes} onChange={(e) => setContactDraft({ ...contactDraft, notes: e.target.value })} />
                                    </div>

                                    <div className="field xl:col-span-3">
                                        <label>Notas del vínculo con esta empresa</label>
                                        <textarea rows={2} value={contactDraft.link_notes} onChange={(e) => setContactDraft({ ...contactDraft, link_notes: e.target.value })} />
                                    </div>
                                </div>

                                <div className="px-4 py-3 border-t border-[#0b4d78] flex flex-wrap justify-end gap-2">
                                    <button type="button" onClick={() => setContactEditorOpen(false)} className="px-3 py-1.5 rounded-lg bg-[#04192b] border border-[#0b4d78] text-xs">
                                        Cancelar
                                    </button>
                                    <button disabled={savingContactMaster} type="submit" className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-white text-xs hover:bg-[#d4af37]Light">
                                        {savingContactMaster ? "Guardando..." : contactEditorMode === "edit" ? "Guardar contacto" : "Crear y vincular"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </>
                ) : null}

                {editOpen && companyDraft ? (
                    <>
                        <div
                            onClick={() => setEditOpen(false)}
                            style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.45)", zIndex: 2000 }}
                        />
                        <div
                            style={{
                                position: "fixed",
                                inset: 0,
                                zIndex: 2100,
                                display: "grid",
                                placeItems: "center",
                                padding: 16,
                            }}
                        >
                            <form
                                onSubmit={saveCompanyData}
                                className="bg-[#04192b] border border-[#0b4d78] rounded-2xl shadow-2xl"
                                style={{ width: "min(1120px, 96vw)", maxHeight: "92vh", overflow: "auto" }}
                            >
                                <div className="px-4 py-3 border-b border-[#0b4d78] flex items-center justify-between gap-3">
                                    <div>
                                        <h3 className="text-sm font-semibold text-white">Editar datos de entidad</h3>
                                        <p className="text-[11px] text-[#9fc4df]">Datos generales, eje, sección, pipeline y responsable.</p>
                                    </div>
                                    <button type="button" onClick={() => setEditOpen(false)} className="px-3 py-1.5 rounded-lg bg-[#04192b] border border-[#0b4d78] text-xs">
                                        Cerrar
                                    </button>
                                </div>

                                <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 text-xs">
                                    <div className="field xl:col-span-2">
                                        <label>Empresa</label>
                                        <input value={companyDraft.name} onChange={(e) => setCompanyDraft({ ...companyDraft, name: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Estatus pipeline</label>
                                        <select value={companyDraft.status} onChange={(e) => setCompanyDraft({ ...companyDraft, status: e.target.value })}>
                                            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>

                                    <div className="field">
                                        <label>Región</label>
                                        <input value={companyDraft.region} onChange={(e) => setCompanyDraft({ ...companyDraft, region: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Ciudad</label>
                                        <input value={companyDraft.city} onChange={(e) => setCompanyDraft({ ...companyDraft, city: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Teléfono</label>
                                        <input value={companyDraft.phone} onChange={(e) => setCompanyDraft({ ...companyDraft, phone: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Email</label>
                                        <input value={companyDraft.email} onChange={(e) => setCompanyDraft({ ...companyDraft, email: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Sitio web</label>
                                        <input value={companyDraft.website} onChange={(e) => setCompanyDraft({ ...companyDraft, website: e.target.value })} />
                                    </div>

                                    <div className="field">
                                        <label>Eje / industria</label>
                                        <select
                                            value={companyDraft.industry_id}
                                            onChange={(e) => {
                                                const nextIndustry = e.target.value;
                                                setCompanyDraft({ ...companyDraft, industry_id: nextIndustry, section_id: "" });
                                                loadSections(nextIndustry);
                                            }}
                                        >
                                            <option value="">— Sin eje —</option>
                                            {industries.map((i) => (
                                                <option key={i.id} value={String(i.id)}>
                                                    {(i.code ? `${i.code}: ` : "") + (i.name || `Eje ${i.id}`)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="field">
                                        <label>Sección del eje</label>
                                        <select
                                            value={companyDraft.section_id}
                                            onChange={(e) => setCompanyDraft({ ...companyDraft, section_id: e.target.value })}
                                            disabled={!companyDraft.industry_id}
                                        >
                                            <option value="">— Sin sección —</option>
                                            {sections.map((s) => (
                                                <option key={s.id} value={String(s.id)}>
                                                    {(s.title || s.name || `Sección ${s.id}`) + (s.description ? `: ${s.description}` : "")}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {canAdmin ? (
                                        <div className="field">
                                            <label>PM responsable</label>
                                            <select value={companyDraft.pm_user_id} onChange={(e) => setCompanyDraft({ ...companyDraft, pm_user_id: e.target.value })}>
                                                <option value="">— Sin PM —</option>
                                                {pmUsers.map((u) => (
                                                    <option key={u.id} value={String(u.id)}>
                                                        {u.display_name || u.email}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ) : null}

                                    <div className="field xl:col-span-3">
                                        <label>Dirección</label>
                                        <input value={companyDraft.address} onChange={(e) => setCompanyDraft({ ...companyDraft, address: e.target.value })} />
                                    </div>

                                    <div className="field xl:col-span-3">
                                        <label>Rubro / sector</label>
                                        <textarea rows={2} value={companyDraft.sector_text} onChange={(e) => setCompanyDraft({ ...companyDraft, sector_text: e.target.value })} />
                                    </div>

                                    <div className="field xl:col-span-3">
                                        <label>Notas</label>
                                        <textarea rows={3} value={companyDraft.notes} onChange={(e) => setCompanyDraft({ ...companyDraft, notes: e.target.value })} />
                                    </div>
                                </div>

                                <div className="px-4 py-3 border-t border-[#0b4d78] flex flex-wrap justify-end gap-2">
                                    <button type="button" onClick={() => setEditOpen(false)} className="px-3 py-1.5 rounded-lg bg-[#04192b] border border-[#0b4d78] text-xs">
                                        Cancelar
                                    </button>
                                    <button disabled={savingCompany} type="submit" className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-white text-xs hover:bg-[#d4af37]Light">
                                        {savingCompany ? "Guardando..." : "Guardar cambios"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </>
                ) : null}

            </div>
        </MainLayout>
    );
}
